import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import { ServiceLog, ServiceLogHideData, ServiceLogResponse } from '../@types/service.type';
import {
  getServiceLogsByServiceId,
  hideServiceLog,
  getServiceLogsById,
  updateServiceLogsById,
  createServiceLogsForService,
} from '../api/services';
import { ServiceLogAllKey, ServiceLogOneKey } from '../configs/services';

interface HideServiceLogRequest extends ServiceLogHideData {
  id: number;
}

// The base `ServiceLog` type is narrow (`{ id?, notes }`), but callers of the
// add-log mutation also pass optional `isInternal` / `serviceId` fields that
// the BFF accepts. Widen at the mutation boundary rather than the shared
// `ServiceLog` type so unrelated consumers stay unchanged.
interface AddServiceLogRequest extends ServiceLog {
  isInternal?: boolean;
  serviceId?: number;
}

export const useGetServiceLogsByServiceId = (id: number): UseQueryResult<ServiceLogResponse[]> => {
  return useQuery({
    queryKey: [ServiceLogAllKey, id],
    queryFn: ({ signal }) => getServiceLogsByServiceId(id, signal),
    enabled: id !== 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useGetServiceLogsById = (id: number): UseQueryResult<ServiceLogResponse> => {
  return useQuery({
    queryKey: [ServiceLogOneKey, id],
    queryFn: ({ signal }) => getServiceLogsById(id, signal),
    enabled: false,
  });
};

export const useHideServiceLog = (): UseMutationResult<void, Error, HideServiceLogRequest> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: HideServiceLogRequest) => {
      return await hideServiceLog(data.id, { isHidden: data.isHidden });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey],
      });
    },
  });
};

export const useAddServiceLogsForService = (
  serviceId: number
): UseMutationResult<ServiceLogResponse, Error, AddServiceLogRequest> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddServiceLogRequest) => {
      return await createServiceLogsForService(serviceId, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId],
      });
    },
  });
};

// Update service log input type
interface ServiceLogUpdateData {
  id: number;
  serviceId: number;
  notes?: string;
  isHidden?: boolean;
}

export const useUpdateServiceLogsById = (): UseMutationResult<
  ServiceLogResponse,
  Error,
  ServiceLogUpdateData
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceLogUpdateData) => {
      return await updateServiceLogsById(data.id, data);
    },

    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, variables.serviceId],
      });
    },
  });
};
